import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  HardDriveDownload,
  RefreshCw,
  AlertTriangle,
  Search,
} from 'lucide-react';
import baseapi from '../../../services/baseapi';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  ComposedChart,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

export interface ClickhouseIngestionProps {
  activeCluster: string;
  timeRange?: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

export interface IngestionWindow {
  from?: string;
  to?: string;
  previous_from?: string;
}

export interface IngestionLive {
  running_queries?: number;
  tcp_connections?: number;
  http_connections?: number;
  pending_async_inserts?: number;
  async_insert_threads_active?: number;
  delayed_inserts?: number;
  running_merges?: number;
  parts_active?: number;
  parts_outdated?: number;
  memory_tracking_bytes?: number;
  async_insert_queue_size?: number;
  async_insert_queue_bytes?: number;
  fresh?: boolean;
}

export interface IngestionCoverage {
  collection_started?: string | null;
  current_window_pct?: number | null;
  previous_window_pct?: number | null;
  previous_window_compared?: boolean;
  reason?: string | null;
  async_counted?: boolean;
  async_reason?: string | null;
}

export interface IngestionTableItem {
  database: string;
  table: string;
  rows: number;
  bytes_on_disk: number;
  active_parts: number;
}

export interface IngestionKPIs {
  total_rows?: number | null;
  total_rows_change_pct?: number | null;
  throughput_rows_per_sec?: number | null;
  throughput_change_pct?: number | null;
  active_sources?: number | null;
  active_sources_change_pct?: number | null;
  failed_inserts?: number | null;
  failed_inserts_change_pct?: number | null;
  successful_inserts?: number | null;
  async_inserts?: number | null;
  async_flushes?: number | null;
  async_flush_failures?: number | null;
  bytes_written?: number | null;
  avg_insert_latency_ms?: number | null;
  parts_created?: number | null;
  part_rows?: number | null;
  part_errors?: number | null;
}

export interface IngestionSeries {
  labels?: string[];
  rows_per_sec?: (number | null)[];
  avg_latency_ms?: (number | null)[];
  failed_inserts?: (number | null)[];
  failed_async_flushes?: (number | null)[];
}

export interface IngestionData {
  window?: IngestionWindow;
  capability?: Record<string, string>;
  capability_state?: Record<string, string>;
  capability_reason?: Record<string, string | null>;
  coverage?: IngestionCoverage;
  live?: IngestionLive;
  async_queue?: Record<string, any>;
  tables?: IngestionTableItem[];
  kpis?: IngestionKPIs;
  throughput_series?: IngestionSeries;
  rate_latency_series?: IngestionSeries;
  by_source?: any[];
  by_table?: any[];
  by_format?: any[];
  kafka_consumers?: any[];
  async_inserts?: Record<string, any> | any[];
  recent_activity?: any[];
  recent_errors?: any[];
  errors_by_code?: any[];
  [key: string]: any;
}

export interface IngestionResponse {
  status?: boolean;
  applicable?: boolean;
  data?: IngestionData;
  [key: string]: any;
}

// Helpers
const formatBytes = (bytes: number | null | undefined): string => {
  if (bytes === null || bytes === undefined || isNaN(bytes)) return '—';
  const num = Number(bytes);
  if (num === 0) return '0 B';
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB'];
  let i = 0;
  let val = num;
  while (val >= 1024 && i < units.length - 1) {
    val /= 1024;
    i++;
  }
  return `${i === 0 ? val : val.toFixed(1)} ${units[i]}`;
};

const formatNumber = (val: number | null | undefined, decimals = 0): string => {
  if (val === null || val === undefined || isNaN(val)) return '—';
  return Number(val).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
};

const formatPctChange = (pct: number | null | undefined): string => {
  if (pct === null || pct === undefined || isNaN(pct)) return 'vs prev hour: n/a';
  const sign = pct > 0 ? '+' : '';
  return `vs prev hour: ${sign}${Number(pct).toFixed(1)}%`;
};

// Recharts Custom Tooltips
const ChartTooltip: React.FC<any> = ({ active, payload, label, isDark }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className={`p-2.5 rounded-lg shadow-xl text-xs min-w-[140px] z-50 space-y-1.5 border backdrop-blur-sm ${
          isDark
            ? 'bg-gray-900/95 border-gray-700 text-gray-100 shadow-black/50'
            : 'bg-white/95 border-gray-200 text-gray-900 shadow-gray-200/50'
        }`}
      >
        <div
          className={`font-bold border-b pb-1 ${
            isDark ? 'border-gray-800 text-gray-200' : 'border-gray-100 text-gray-800'
          }`}
        >
          {label}
        </div>
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center justify-between gap-3 text-[11px]">
            <span className={`flex items-center gap-1.5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: entry.color || entry.stroke || entry.fill }}
              />
              {entry.name}:
            </span>
            <span className={`font-mono font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {entry.value !== null && entry.value !== undefined ? entry.value : '—'}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export const ClickhouseIngestion: React.FC<ClickhouseIngestionProps> = ({
  activeCluster,
  timeRange = '60',
  clientId,
  refreshKey,
  dbType = 'clickhouse',
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<IngestionData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Dynamic Dark/Light Theme Detection via MutationObserver
  const [isDark, setIsDark] = useState<boolean>(() =>
    typeof document !== 'undefined' ? document.documentElement.classList.contains('dark') : false
  );

  useEffect(() => {
    const checkDark = () => setIsDark(document.documentElement.classList.contains('dark'));
    checkDark();
    const observer = new MutationObserver(checkDark);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const chartTheme = useMemo(() => {
    return {
      grid: isDark ? '#4b5563' : '#e2e8f0',
      gridOpacity: isDark ? 0.45 : 0.8,
      axisStroke: isDark ? '#4b5563' : '#cbd5e1',
      axisText: isDark ? '#9ca3af' : '#475569',
      pieStroke: isDark ? '#1f2937' : '#ffffff',
      tooltipBg: isDark ? '#111827' : '#ffffff',
      tooltipBorder: isDark ? '#374151' : '#e5e7eb',
      tooltipText: isDark ? '#f3f4f6' : '#111827',
      tooltipMuted: isDark ? '#9ca3af' : '#6b7280',
    };
  }, [isDark]);

  // Filters & State
  const [activeDetailTab, setActiveDetailTab] = useState<string>('by_source');
  const [detailSearch, setDetailSearch] = useState<string>('');
  const [tableSearch, setTableSearch] = useState<string>('');

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);
    setError(null);
    try {
      const minutes = parseInt(timeRange || '60', 10);
      const params: any = {
        cluster_id: activeCluster,
        minutes: isNaN(minutes) ? 60 : minutes,
      };
      if (clientId) {
        params.client_id = clientId;
      }

      const res = await baseapi.get('/db_monitor_dashboard/clickhouse/ingestion', { params });
      if (res?.data) {
        const payload: IngestionData =
          res.data.data !== undefined ? res.data.data : res.data;
        setData(payload);
      }
    } catch (err: any) {
      console.error('Failed to fetch ClickHouse ingestion telemetry:', err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to load ClickHouse ingestion telemetry'
      );
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  // Derived references
  const kpis = data?.kpis;
  const live = data?.live;
  const coverage = data?.coverage;
  const capability = data?.capability || data?.capability_state || {};
  const rawTables = useMemo(() => data?.tables || [], [data?.tables]);

  // Capability OK count
  const capabilityList = useMemo(() => {
    return Object.entries(capability);
  }, [capability]);

  const okCapabilityCount = useMemo(() => {
    return capabilityList.filter(([_, val]) => String(val).toLowerCase() === 'ok').length;
  }, [capabilityList]);

  // Filtered tables
  const filteredTables = useMemo(() => {
    if (!tableSearch.trim()) return rawTables;
    const q = tableSearch.toLowerCase();
    return rawTables.filter(
      (t) =>
        t.table.toLowerCase().includes(q) ||
        t.database.toLowerCase().includes(q)
    );
  }, [rawTables, tableSearch]);

  // Throughput Series Chart Data
  const throughputChartData = useMemo(() => {
    const labels = data?.throughput_series?.labels || [];
    const rows = data?.throughput_series?.rows_per_sec || [];
    return labels.map((iso, idx) => {
      let timeLabel = '';
      try {
        const d = new Date(iso);
        timeLabel = !isNaN(d.getTime())
          ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
          : iso;
      } catch {
        timeLabel = iso;
      }
      const val = rows[idx];
      return {
        time: timeLabel,
        'Rows/s': val !== null && val !== undefined ? Number(val) : 0,
      };
    });
  }, [data?.throughput_series]);

  const isThroughputFlatZero = useMemo(() => {
    return (
      throughputChartData.length === 0 ||
      throughputChartData.every((item) => (item['Rows/s'] || 0) === 0)
    );
  }, [throughputChartData]);

  // Failed Inserts & Latency Chart Data
  const rateLatencyChartData = useMemo(() => {
    const labels = data?.rate_latency_series?.labels || [];
    const failed = data?.rate_latency_series?.failed_inserts || [];
    const lat = data?.rate_latency_series?.avg_latency_ms || [];
    return labels.map((iso, idx) => {
      let timeLabel = '';
      try {
        const d = new Date(iso);
        timeLabel = !isNaN(d.getTime())
          ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
          : iso;
      } catch {
        timeLabel = iso;
      }
      return {
        time: timeLabel,
        'Failed inserts': failed[idx] !== null && failed[idx] !== undefined ? Number(failed[idx]) : 0,
        'Avg latency (ms)': lat[idx] !== null && lat[idx] !== undefined ? Number(lat[idx]) : null,
      };
    });
  }, [data?.rate_latency_series]);

  const isLatencyAndFailFlatZero = useMemo(() => {
    return (
      rateLatencyChartData.length === 0 ||
      rateLatencyChartData.every(
        (item) => item['Failed inserts'] === 0 && item['Avg latency (ms)'] === null
      )
    );
  }, [rateLatencyChartData]);

  // Top Tables by Rows (Top 8)
  const topTablesByRows = useMemo(() => {
    return [...rawTables]
      .sort((a, b) => (b.rows || 0) - (a.rows || 0))
      .slice(0, 8)
      .map((t) => ({
        name: t.table,
        fullName: `${t.database}.${t.table}`,
        rows: t.rows || 0,
      }));
  }, [rawTables]);

  // Top Tables by Size on Disk (Top 8)
  const topTablesBySize = useMemo(() => {
    return [...rawTables]
      .sort((a, b) => (b.bytes_on_disk || 0) - (a.bytes_on_disk || 0))
      .slice(0, 8)
      .map((t) => ({
        name: t.table,
        fullName: `${t.database}.${t.table}`,
        bytes: t.bytes_on_disk || 0,
        formatted: formatBytes(t.bytes_on_disk),
      }));
  }, [rawTables]);

  // Parts Doughnut Data
  const partsDoughnutData = useMemo(() => {
    const active = live?.parts_active || 0;
    const outdated = live?.parts_outdated || 0;
    return [
      { name: 'Active', value: active, color: '#f59e0b' },
      { name: 'Outdated', value: outdated, color: '#126ff1ff' },
    ];
  }, [live?.parts_active, live?.parts_outdated]);

  const totalParts = (live?.parts_active || 0) + (live?.parts_outdated || 0);

  // Ingestion Detail Sections configuration
  const detailTabs = useMemo(() => {
    const getCount = (val: any): number => {
      if (!val) return 0;
      if (Array.isArray(val)) return val.length;
      if (typeof val === 'object') return Object.keys(val).length;
      return 0;
    };

    return [
      { key: 'by_source', label: 'By source', count: getCount(data?.by_source) },
      { key: 'by_table', label: 'By table', count: getCount(data?.by_table) },
      { key: 'by_format', label: 'By format', count: getCount(data?.by_format) },
      { key: 'kafka_consumers', label: 'Kafka consumers', count: getCount(data?.kafka_consumers) },
      { key: 'async_inserts', label: 'Async inserts', count: getCount(data?.async_inserts) },
      { key: 'recent_activity', label: 'Recent activity', count: getCount(data?.recent_activity) },
      { key: 'recent_errors', label: 'Recent errors', count: getCount(data?.recent_errors) },
      { key: 'errors_by_code', label: 'Errors by code', count: getCount(data?.errors_by_code) },
    ];
  }, [data]);

  // Active detail content rendering
  const activeDetailContent = useMemo(() => {
    if (!data) return { rows: [], cols: [], isObjectMap: false };
    const val = data[activeDetailTab];

    if (!val || (Array.isArray(val) && val.length === 0) || (typeof val === 'object' && Object.keys(val).length === 0)) {
      return { rows: [], cols: [], isObjectMap: false };
    }

    if (Array.isArray(val)) {
      const allCols = Array.from(new Set(val.flatMap((o) => (o && typeof o === 'object' ? Object.keys(o) : []))));
      const q = detailSearch.trim().toLowerCase();
      const filtered = val.filter((item) => {
        if (!q) return true;
        return Object.values(item || {}).some((v) =>
          String(v ?? '').toLowerCase().includes(q)
        );
      });
      return { rows: filtered, cols: allCols, isObjectMap: false };
    }

    // Object map
    const entries = Object.entries(val);
    const q = detailSearch.trim().toLowerCase();
    const filtered = entries.filter(([k, v]) => {
      if (!q) return true;
      return (
        k.toLowerCase().includes(q) ||
        String(v ?? '').toLowerCase().includes(q)
      );
    });
    return {
      rows: filtered.map(([key, value]) => ({ key, value })),
      cols: ['key', 'value'],
      isObjectMap: true,
    };
  }, [data, activeDetailTab, detailSearch]);

  // Loading Skeleton State
  if (loading && !data) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between pb-3 border-b border-gray-200 dark:border-gray-800 animate-pulse">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gray-200 dark:bg-gray-800" />
            <div className="space-y-2">
              <div className="h-4 w-48 bg-gray-200 dark:bg-gray-800 rounded" />
              <div className="h-3 w-64 bg-gray-200 dark:bg-gray-800 rounded" />
            </div>
          </div>
          <div className="h-9 w-32 bg-gray-200 dark:bg-gray-800 rounded" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2.5">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div
              key={i}
              className="h-24 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 animate-pulse"
            />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
          <div className="lg:col-span-5 h-64 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 animate-pulse" />
          <div className="lg:col-span-4 h-64 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 animate-pulse" />
          <div className="lg:col-span-3 h-64 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 animate-pulse" />
        </div>
      </div>
    );
  }

  // Error State
  if (error && !data) {
    return (
      <div className="p-8 rounded-xl bg-rose-50 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-900/40 text-center space-y-4">
        <AlertTriangle className="w-10 h-10 text-rose-500 mx-auto" />
        <div>
          <h3 className="text-base font-semibold text-rose-900 dark:text-rose-200">
            Failed to Load ClickHouse Ingestion Telemetry
          </h3>
          <p className="text-sm text-rose-600 dark:text-rose-400 mt-1 max-w-lg mx-auto">
            {error}
          </p>
        </div>
        <button
          onClick={() => fetchData()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold shadow-sm transition-colors cursor-pointer"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* ─────────────────────────────────────────────────────────────
          HEADER BANNER & CONTROLS
          ───────────────────────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 pb-3 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <div className="p-1.5 rounded-lg bg-orange-500 text-white shadow-sm">
            <HardDriveDownload className="w-4 h-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                Ingestion
              </h2>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-orange-50 dark:bg-orange-500/15 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-500/30">
                {(dbType || 'CLICKHOUSE').toUpperCase()}
              </span>
            </div>
           
          </div>
        </div>

        {/* Status Pill & Refresh Button */}
        <div className="flex items-center flex-wrap gap-2.5">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 shadow-sm">
            <span
              className={`w-2 h-2 rounded-full ${
                okCapabilityCount > 0 ? 'bg-emerald-500' : 'bg-amber-500'
              }`}
            />
            {okCapabilityCount} / {capabilityList.length || 5} log sources OK
          </span>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          ROW 0: 8 TOP KPI CARDS
          ───────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-1">
        {/* KPI 1: Rows Ingested */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200">
            Rows ingested
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono tracking-tight my-1">
            {formatNumber(kpis?.total_rows)}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-200 truncate">
            {formatPctChange(kpis?.total_rows_change_pct)}
          </div>
        </div>

        {/* KPI 2: Throughput */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm  flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200">
            Throughput
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono tracking-tight my-1">
            {formatNumber(kpis?.throughput_rows_per_sec, 1)} <span className="text-xs font-normal text-gray-600 dark:text-gray-200">rows/s</span>
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-200 truncate">
            {formatPctChange(kpis?.throughput_change_pct)}
          </div>
        </div>

        {/* KPI 3: Active Sources */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200">
            Active sources
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono tracking-tight my-1">
            {formatNumber(kpis?.active_sources)}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-200 truncate">
            {formatPctChange(kpis?.active_sources_change_pct)}
          </div>
        </div>

        {/* KPI 4: Successful Inserts */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm  flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200">
            Successful inserts
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono tracking-tight my-1">
            {formatNumber(kpis?.successful_inserts)}
          </div>
          <div className="text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold truncate">
            {kpis?.async_inserts ? `${formatNumber(kpis.async_inserts)} async` : 'all sync'}
          </div>
        </div>

        {/* KPI 5: Failed Inserts */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200">
            Failed inserts
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono tracking-tight my-1">
            {formatNumber(kpis?.failed_inserts)}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-200 truncate">
            {formatPctChange(kpis?.failed_inserts_change_pct)}
          </div>
        </div>

        {/* KPI 6: Bytes Written */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200">
            Bytes written
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono tracking-tight my-1">
            {formatBytes(kpis?.bytes_written)}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-200 truncate">
            disk throughput
          </div>
        </div>

        {/* KPI 7: Avg Insert Latency */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200">
            Avg insert latency
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono tracking-tight my-1">
            {kpis?.avg_insert_latency_ms !== null && kpis?.avg_insert_latency_ms !== undefined
              ? `${formatNumber(kpis.avg_insert_latency_ms, 1)} ms`
              : '—'}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-200 truncate">
            {kpis?.avg_insert_latency_ms !== null ? 'measured sample' : 'no samples'}
          </div>
        </div>

        {/* KPI 8: Parts Created */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200">
            Parts created
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono tracking-tight my-1">
            {formatNumber(kpis?.parts_created)}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-200 truncate">
            {formatNumber(kpis?.part_rows)} rows · {formatNumber(kpis?.part_errors)} err
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          ROW 1: THROUGHPUT, FAILED INSERTS & LATENCY, LIVE NOW
          ───────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* Panel 1: Throughput (5 cols) */}
        <div className="lg:col-span-5 p-3.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
              Throughput
              <span className="text-[11px] normal-case font-normal text-gray-500 dark:text-gray-200">
                area · rows per second · per minute
              </span>
            </h3>
          </div>

          <div className="h-56 w-full relative">
            {isThroughputFlatZero && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="text-xs text-gray-500 dark:text-gray-300 bg-white/90 dark:bg-gray-800/90 border border-gray-200 dark:border-gray-700 shadow-sm px-2.5 py-1 rounded-md font-medium">
                  0 rows/s throughout the window
                </span>
              </div>
            )}
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={throughputChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="throughputGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={isDark ? 0.45 : 0.3} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={chartTheme.grid}
                  strokeOpacity={chartTheme.gridOpacity}
                  vertical={false}
                />
                <XAxis
                  dataKey="time"
                  tick={{ fontSize: 10, fill: chartTheme.axisText }}
                  tickLine={{ stroke: chartTheme.axisStroke }}
                  axisLine={{ stroke: chartTheme.axisStroke }}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: chartTheme.axisText }}
                  tickLine={{ stroke: chartTheme.axisStroke }}
                  axisLine={{ stroke: chartTheme.axisStroke }}
                  domain={[0, 'auto']}
                />
                <RechartsTooltip content={<ChartTooltip isDark={isDark} />} />
                <Area
                  type="monotone"
                  dataKey="Rows/s"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#throughputGrad)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Panel 2: Failed Inserts & Latency (4 cols) */}
        <div className="lg:col-span-4 p-3.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
              Failed inserts & latency
              <span className="text-[11px] normal-case font-normal text-gray-500 dark:text-gray-200">
                bars = failed · line = avg ms
              </span>
            </h3>
          </div>

          <div className="h-56 w-full relative">
            {isLatencyAndFailFlatZero && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="text-xs text-gray-500 dark:text-gray-300 bg-white/90 dark:bg-gray-800/90 border border-gray-200 dark:border-gray-700 shadow-sm px-2.5 py-1 rounded-md font-medium">
                  0 failures · no latency samples
                </span>
              </div>
            )}
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={rateLatencyChartData} margin={{ top: 10, right: -10, left: -25, bottom: 0 }}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={chartTheme.grid}
                  strokeOpacity={chartTheme.gridOpacity}
                  vertical={false}
                />
                <XAxis
                  dataKey="time"
                  tick={{ fontSize: 10, fill: chartTheme.axisText }}
                  tickLine={{ stroke: chartTheme.axisStroke }}
                  axisLine={{ stroke: chartTheme.axisStroke }}
                />
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: 10, fill: chartTheme.axisText }}
                  tickLine={{ stroke: chartTheme.axisStroke }}
                  axisLine={{ stroke: chartTheme.axisStroke }}
                  domain={[0, 'auto']}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 10, fill: chartTheme.axisText }}
                  tickLine={{ stroke: chartTheme.axisStroke }}
                  axisLine={{ stroke: chartTheme.axisStroke }}
                  domain={[0, 'auto']}
                />
                <RechartsTooltip content={<ChartTooltip isDark={isDark} />} />
                <Bar yAxisId="left" dataKey="Failed inserts" fill="#f06a6a" radius={[2, 2, 0, 0]} barSize={8} />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="Avg latency (ms)"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ r: 2 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Panel 3: Live Now (3 cols) */}
        <div className="lg:col-span-3 p-3.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              Live now
              <span className="text-[10px] px-1.5 py-0.2 rounded font-semibold bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30">
                fresh
              </span>
            </h3>
          </div>

          <div className="grid grid-cols-2 gap-y-1.5 gap-x-2 text-xs py-1">
            <span className="text-gray-500 dark:text-gray-200">Running queries</span>
            <span className="text-right font-mono font-bold text-gray-900 dark:text-white">
              {formatNumber(live?.running_queries)}
            </span>

            <span className="text-gray-500 dark:text-gray-200">Running merges</span>
            <span className="text-right font-mono font-bold text-gray-900 dark:text-white">
              {formatNumber(live?.running_merges)}
            </span>

            <span className="text-gray-500 dark:text-gray-200">HTTP connections</span>
            <span className="text-right font-mono font-bold text-gray-900 dark:text-white">
              {formatNumber(live?.http_connections)}
            </span>

            <span className="text-gray-500 dark:text-gray-200">TCP connections</span>
            <span className="text-right font-mono font-bold text-gray-900 dark:text-white">
              {formatNumber(live?.tcp_connections)}
            </span>

            <span className="text-gray-500 dark:text-gray-200">Pending async inserts</span>
            <span className="text-right font-mono font-bold text-gray-900 dark:text-white">
              {formatNumber(live?.pending_async_inserts)}
            </span>

            <span className="text-gray-500 dark:text-gray-200">Async insert threads</span>
            <span className="text-right font-mono font-bold text-gray-900 dark:text-white">
              {formatNumber(live?.async_insert_threads_active)}
            </span>

            <span className="text-gray-500 dark:text-gray-200">Delayed inserts</span>
            <span className="text-right font-mono font-bold text-gray-900 dark:text-white">
              {formatNumber(live?.delayed_inserts)}
            </span>

            <span className="text-gray-500 dark:text-gray-200">Memory tracked</span>
            <span className="text-right font-mono font-bold text-gray-900 dark:text-white">
              {formatBytes(live?.memory_tracking_bytes)}
            </span>
          </div>

          <div className="pt-2 border-t border-gray-100 dark:border-gray-700/60 flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-200">
            <span>Parts: {formatNumber(live?.parts_active)} active</span>
            <span>{formatNumber(live?.parts_outdated)} outdated</span>
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          ROW 2: DATA PARTS, ROWS PER TABLE, SIZE ON DISK, LOG SOURCES & COVERAGE
          ───────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-3">
        {/* Panel 4: Data Parts Doughnut (3 cols) */}
        <div className="lg:col-span-3 p-3.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
              Data parts
             
            </h3>
          </div>

          <div className="h-44 w-full relative flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={partsDoughnutData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={46}
                  outerRadius={68}
                  strokeWidth={2}
                  stroke={chartTheme.pieStroke}
                >
                  {partsDoughnutData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip content={<ChartTooltip isDark={isDark} />} />
              </PieChart>
            </ResponsiveContainer>
            {/* Center label */}
            <div className="absolute flex flex-col items-center justify-center pointer-events-none">
              <span className="text-base font-bold font-mono text-gray-900 dark:text-white leading-tight">
                {formatNumber(totalParts)}
              </span>
              <span className="text-[10px] text-gray-500 dark:text-gray-200">parts</span>
            </div>
          </div>

          <div className="flex items-center justify-center gap-4 text-xs pt-1">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
              <span className="text-gray-700 dark:text-gray-300">Active ({formatNumber(live?.parts_active)})</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-500" />
              <span className="text-gray-700 dark:text-gray-300">Outdated ({formatNumber(live?.parts_outdated)})</span>
            </div>
          </div>
        </div>

        {/* Panel 5: Rows Per Table (3 cols) */}
        <div className="lg:col-span-3 p-3.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
              Rows per table
              
            </h3>
          </div>

          <div className="h-48 w-full">
            {topTablesByRows.length === 0 ? (
              <div className="h-full flex items-center justify-center text-xs text-gray-200">
                No table data
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={topTablesByRows}
                  layout="vertical"
                  margin={{ top: 5, right: 25, left: -5, bottom: 5 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={chartTheme.grid}
                    strokeOpacity={chartTheme.gridOpacity}
                    horizontal={false}
                  />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 9, fill: chartTheme.axisText }}
                    tickLine={{ stroke: chartTheme.axisStroke }}
                    axisLine={{ stroke: chartTheme.axisStroke }}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={70}
                    tick={{ fontSize: 9, fill: chartTheme.axisText }}
                    tickLine={{ stroke: chartTheme.axisStroke }}
                    axisLine={{ stroke: chartTheme.axisStroke }}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: chartTheme.tooltipBg,
                      borderColor: chartTheme.tooltipBorder,
                      color: chartTheme.tooltipText,
                      borderRadius: '8px',
                      fontSize: '11px',
                      boxShadow: isDark
                        ? '0 10px 15px -3px rgba(0, 0, 0, 0.5)'
                        : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                    }}
                    itemStyle={{
                      color: chartTheme.tooltipText,
                    }}
                    labelStyle={{
                      color: chartTheme.tooltipMuted,
                      fontWeight: 600,
                    }}
                    formatter={(val: any, _: any, item: any) => [
                      formatNumber(val),
                      item.payload.fullName || 'Rows',
                    ]}
                     cursor={{ fill: isDark ? '#334155' : '#e2e8f0', opacity: 0.3 }}
                  />
                  <Bar dataKey="rows" fill="#3b82f6" radius={[0, 4, 4, 0]} barSize={10} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Panel 6: Size on Disk Per Table (3 cols) */}
        <div className="lg:col-span-3 p-3.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
              Size on disk per table
            
            </h3>
          </div>

          <div className="h-48 w-full">
            {topTablesBySize.length === 0 ? (
              <div className="h-full flex items-center justify-center text-xs text-gray-200">
                No table data
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={topTablesBySize}
                  layout="vertical"
                  margin={{ top: 5, right: 35, left: -5, bottom: 5 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={chartTheme.grid}
                    strokeOpacity={chartTheme.gridOpacity}
                    horizontal={false}
                  />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 9, fill: chartTheme.axisText }}
                    tickFormatter={(v) => formatBytes(v)}
                    tickLine={{ stroke: chartTheme.axisStroke }}
                    axisLine={{ stroke: chartTheme.axisStroke }}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={70}
                    tick={{ fontSize: 9, fill: chartTheme.axisText }}
                    tickLine={{ stroke: chartTheme.axisStroke }}
                    axisLine={{ stroke: chartTheme.axisStroke }}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: chartTheme.tooltipBg,
                      borderColor: chartTheme.tooltipBorder,
                      color: chartTheme.tooltipText,
                      borderRadius: '8px',
                      fontSize: '11px',
                      boxShadow: isDark
                        ? '0 10px 15px -3px rgba(0, 0, 0, 0.5)'
                        : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                    }}
                    itemStyle={{
                      color: chartTheme.tooltipText,
                    }}
                    labelStyle={{
                      color: chartTheme.tooltipMuted,
                      fontWeight: 600,
                    }}
                    formatter={(_: any, __: any, item: any) => [
                      item.payload.formatted,
                      item.payload.fullName || 'Disk Size',
                    ]}
                    cursor={{ fill: isDark ? '#334155' : '#e2e8f0', opacity: 0.3 }}
                  />
                  <Bar dataKey="bytes" fill="#a855f7" radius={[0, 4, 4, 0]} barSize={10} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Panel 7: Log Sources & Coverage (3 cols) */}
        <div className="lg:col-span-3 p-3.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div>
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-2">
              Log sources & coverage
            </h3>

            {/* Log sources status */}
            <div className="space-y-1 max-h-24 overflow-y-auto pr-1">
              {capabilityList.map(([source, status]) => (
                <div key={source} className="flex items-center justify-between text-xs py-0.5">
                  <span className="font-mono text-gray-600 dark:text-gray-200 text-[11px] truncate max-w-[160px]" title={source}>
                    {source}
                  </span>
                  <span
                    className={`px-1.5 py-0.2 rounded text-[10px] font-semibold uppercase ${
                      String(status).toLowerCase() === 'ok'
                        ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30'
                        : 'bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30'
                    }`}
                  >
                    {String(status)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Coverage Section */}
          <div className="pt-2 border-t border-gray-100 dark:border-gray-700/60 space-y-1.5 text-xs">


            {/* Current window */}
            <div>
              <div className="flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-200 mb-0.5">
                <span>Current window</span>
                <span className="font-mono font-bold text-gray-800 dark:text-gray-200">
                  {coverage?.current_window_pct != null ? `${coverage.current_window_pct}%` : '—'}
                </span>
              </div>
              <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all duration-500"
                  style={{ width: `${Math.min(coverage?.current_window_pct || 0, 100)}%` }}
                />
              </div>
            </div>

            {/* Previous window */}
            <div>
              <div className="flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-200 mb-0.5">
                <span>Previous window</span>
                <span className="font-mono font-bold text-gray-800 dark:text-gray-200">
                  {coverage?.previous_window_pct != null ? `${coverage.previous_window_pct}%` : '—'}
                </span>
              </div>
              <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all duration-500"
                  style={{ width: `${Math.min(coverage?.previous_window_pct || 0, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          ROW 3: INGESTION DETAIL & TABLES LIST
          ───────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* Panel 8: Ingestion Detail (7 cols) */}
        <div className="lg:col-span-7 p-3.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
              Ingestion detail
            </h3>

            {/* Detail Filter */}
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-gray-200 dark:text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={detailSearch}
                onChange={(e) => setDetailSearch(e.target.value)}
                placeholder="Filter detail..."
                className="bg-gray-50 dark:bg-gray-900/60 border border-gray-300 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1 text-xs text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500 w-44 transition-colors"
              />
            </div>
          </div>

          {/* Tabs header */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-2 border-b border-gray-100 dark:border-gray-700/60 custom-scroll">
            {detailTabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setActiveDetailTab(t.key)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap transition-colors cursor-pointer flex items-center gap-1.5 ${
                  activeDetailTab === t.key
                    ? 'bg-orange-600 text-white font-bold shadow-sm'
                    : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700/50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                <span>{t.label}</span>
                <span
                  className={`text-[10px] px-1 rounded-full ${
                    activeDetailTab === t.key
                      ? 'bg-orange-700 text-white'
                      : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                  }`}
                >
                  {t.count}
                </span>
              </button>
            ))}
          </div>

          {/* Tab Body */}
          <div className="flex-1 overflow-x-auto max-h-64 mt-2">
            {activeDetailContent.rows.length === 0 ? (
              <div className="py-12 text-center text-xs text-gray-200 dark:text-gray-500">
                No {detailTabs.find((t) => t.key === activeDetailTab)?.label.toLowerCase()} in this window.
              </div>
            ) : (
              <table className="w-full border-collapse text-xs text-left">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/80 sticky top-0">
                    {activeDetailContent.cols.map((col) => (
                      <th
                        key={col}
                        className="px-2.5 py-1.5 font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider text-[10px]"
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {activeDetailContent.rows.map((row: any, rIdx: number) => (
                    <tr
                      key={rIdx}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                    >
                      {activeDetailContent.cols.map((col) => {
                        const cellVal = row[col];
                        const displayVal =
                          cellVal === null || cellVal === undefined
                            ? '—'
                            : typeof cellVal === 'object'
                            ? JSON.stringify(cellVal)
                            : String(cellVal);
                        return (
                          <td
                            key={col}
                            className="px-2.5 py-1.5 text-gray-800 dark:text-gray-200 font-mono text-[11px] whitespace-nowrap"
                          >
                            {displayVal}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Panel 9: Tables List (5 cols) */}
        <div className="lg:col-span-5 p-3.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
              Tables
              <span className="text-[11px] normal-case font-normal text-gray-500 dark:text-gray-200">
                {rawTables.length} listed
              </span>
            </h3>

            {/* Tables Filter */}
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-gray-200 dark:text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
                placeholder="Filter tables..."
                className="bg-gray-50 dark:bg-gray-900/60 border border-gray-300 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1 text-xs text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500 w-40 transition-colors"
              />
            </div>
          </div>

          <div className="flex-1 overflow-x-auto max-h-72">
            {filteredTables.length === 0 ? (
              <div className="py-12 text-center text-xs text-gray-200 dark:text-gray-500">
                No matching tables found
              </div>
            ) : (
              <table className="w-full border-collapse text-xs text-left">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/80 sticky top-0">
                    <th className="px-2.5 py-1.5 font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider text-[10px]">
                      Table
                    </th>
                    <th className="px-2.5 py-1.5 font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider text-[10px]">
                      Database
                    </th>
                    <th className="px-2.5 py-1.5 font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider text-[10px] text-right">
                      Rows
                    </th>
                    <th className="px-2.5 py-1.5 font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider text-[10px] text-right">
                      On disk
                    </th>
                    <th className="px-2.5 py-1.5 font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider text-[10px] text-right">
                      Active parts
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {filteredTables.map((t, idx) => (
                    <tr
                      key={`${t.database}-${t.table}-${idx}`}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                    >
                      <td className="px-2.5 py-1.5 text-gray-900 dark:text-white font-medium whitespace-nowrap">
                        {t.table}
                      </td>
                      <td className="px-2.5 py-1.5 text-gray-500 dark:text-gray-200 whitespace-nowrap">
                        {t.database}
                      </td>
                      <td className="px-2.5 py-1.5 text-right font-mono text-gray-800 dark:text-gray-200 whitespace-nowrap">
                        {formatNumber(t.rows)}
                      </td>
                      <td className="px-2.5 py-1.5 text-right font-mono text-gray-800 dark:text-gray-200 whitespace-nowrap">
                        {formatBytes(t.bytes_on_disk)}
                      </td>
                      <td className="px-2.5 py-1.5 text-right font-mono text-gray-800 dark:text-gray-200 whitespace-nowrap">
                        {formatNumber(t.active_parts)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClickhouseIngestion;
